import { memo, useContext } from "react";
import "./PaymentDueSummary.scss";
import { FormattedDate, FormattedMessage } from "react-intl";
import propTypes from "prop-types";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import AmountContainer from "app/pages/.shared/AmountContainer.jsx";
import { PAYMENT_TERMS } from "app/constants";
import SavingsHighlight from "app/pages/.shared/SavingsHighlight/SavingsHighlight";
import Total from "app/pages/.shared/PriceSummary/Total/Total";
import SeparatorLine from "app/components/ui/Separator/SeparatorLine";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";

const PaymentDueSummary = ({
	payNowAmount,
	payAtPropertyAmount,
	paymentMode,
	mensualite2Fois,
	boardDiscount,
	total,
	showPayNowBloc = false,
	publicPrice,
	hideDiscountHBX = false,
}) => {
	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.MEDIUM || resolution === RESOLUTION.SMALL;

	if (!payNowAmount) {
		return null;
	}

	const is2xPaymentMode = paymentMode === PAYMENT_TERMS["2x"];

	const secondPaymentDueDate = is2xPaymentMode && mensualite2Fois?.dueDate;
	const secondPaymentAmount = is2xPaymentMode && mensualite2Fois?.payLaterAmount;

	const parsedSecondPaymentDueDate = new Date(secondPaymentDueDate);
	const isSecondPaymentDueDateValid = !isNaN(parsedSecondPaymentDueDate.getTime());

	return (
		<div className="payment-due-summary" data-testId="payment-due-summary">
			{!isMobile && showPayNowBloc && (
				<>
					<div className="payment-due-summary__group">
						<div className="payment-due-summary__item">
							<Total total={total} taxesAndFeesIncluded />
						</div>
						{boardDiscount > 0 && !hideDiscountHBX && (
							<div className="payment-due-summary__item payment-due-summary__item--savings">
								<SavingsHighlight
									message={
										<FormattedMessage
											id="general.savings.realized.with.amount"
											values={{
												price: <AmountContainer amount={boardDiscount} />,
											}}
										/>
									}
									trackEvent="booking_click_savings_realized"
									trackData={{
										discountValue: boardDiscount,
										publicPrice,
									}}
									description={
										<div className="savings-highlight__description-block">
											<div className="savings-highlight__description-title">
												<FormattedMessage id="savings.tooltip.title" />
											</div>
											<ul className="savings-highlight__list">
												<li>
													<FormattedMessage id="savings.tooltip.line1" />
												</li>
												<li>
													<FormattedMessage id="savings.tooltip.line2" />
												</li>
											</ul>
										</div>
									}
									descriptionAmountLabel={
										<FormattedMessage id="savings.tooltip.amount.label" />
									}
									showDescriptionAmount
									amount={boardDiscount}
								/>
								<div className="payment-due-summary__reference">
									<FormattedMessage
										id="general.instead.of.price.with.amount"
										values={{
											price: <AmountContainer amount={publicPrice} />,
										}}
									/>
								</div>
							</div>
						)}
					</div>
					<SeparatorLine />
				</>
			)}
			<div className="payment-due-summary__group">
				<div className="payment-due-summary__item">
					<Typography variant={TYPOGRAPHY_VARIANTS.LARGE} component="p" isBold>
						<FormattedMessage id="my.bookings.pay.now.label" />
					</Typography>

					<Typography variant={TYPOGRAPHY_VARIANTS.LARGE} component="p" isBold>
						<AmountContainer amount={payNowAmount} />
					</Typography>
				</div>

				{isSecondPaymentDueDateValid && secondPaymentAmount > 0 && (
					<div className="payment-due-summary__item">
						<Typography component="p" variant={TYPOGRAPHY_VARIANTS.REGULAR}>
							<FormattedMessage
								id="payment.pay.later.amount"
								values={{
									dueDate: (
										<FormattedDate
											timeZone="UTC"
											value={parsedSecondPaymentDueDate}
											year="numeric"
											month="2-digit"
											day="2-digit"
										/>
									),
								}}
							/>
						</Typography>

						<Typography component="p" variant={TYPOGRAPHY_VARIANTS.REGULAR}>
							<AmountContainer
								className="amount--normal"
								amount={secondPaymentAmount}
							/>
						</Typography>
					</div>
				)}

				{payAtPropertyAmount > 0 && (
					<div className="payment-due-summary__item">
						<Typography component="p" variant={TYPOGRAPHY_VARIANTS.REGULAR}>
							<FormattedMessage id="pay.at.property" />
						</Typography>

						<Typography component="p" variant={TYPOGRAPHY_VARIANTS.REGULAR}>
							<AmountContainer
								className="amount--normal"
								amount={payAtPropertyAmount}
							/>
						</Typography>
					</div>
				)}
			</div>
		</div>
	);
};

PaymentDueSummary.propTypes = {
	payNowAmount: propTypes.number,
	payAtPropertyAmount: propTypes.number,
	paymentMode: propTypes.number,
	mensualite2Fois: propTypes.object,
	boardDiscount: propTypes.number,
	total: propTypes.number,
	showPayNowBloc: propTypes.bool,
	publicPrice: propTypes.number,
	hideDiscountHBX: propTypes.bool,
};

export default memo(PaymentDueSummary);
